import { useRef, type ReactNode } from "react";
import { Check, Database } from "lucide-react";
import OctermMark from "@/octerm-logo.svg";
import ClaudeMark from "@/logos/claude.svg";
import TeamsMark from "@/logos/microsoft-teams.svg";
import OpenAIMark from "@/logos/openai.svg";
import CopilotMark from "@/logos/microsoft-copilot.svg";
import { REVEAL_STYLES, Tile, useReveal } from "./Bento";

// How Octerm connects: data in from the operator's management software, answers out in their AI
// assistant and in Microsoft Teams. Each picture is drawn in the look of the app it stands for.
// The conversations and counts are sample data.

const ClaudeLogo = ({ className = "" }: { className?: string }) => <img src={ClaudeMark} alt="" className={className} />;
/** Claude, ChatGPT and Copilot side by side: the assistants Octerm's connector works in. */
const AssistantLogos = () => (
  <span className="flex items-center gap-1.5">
    {[ClaudeMark, OpenAIMark, CopilotMark].map((src) => (
      <img key={src} src={src} alt="" className="h-3.5 w-3.5" />
    ))}
  </span>
);
const TeamsLogo = ({ className = "" }: { className?: string }) => <img src={TeamsMark} alt="" className={className} />;

/** The same frame for all three pictures, so they line up across the row. */
function Panel({ className = "", children }: { className?: string; children: ReactNode }) {
  return <div className={`flex h-[184px] flex-col justify-center gap-3 p-4 ${className}`}>{children}</div>;
}

const SYNCED = [
  ["Units", "412"],
  ["Rate changes", "14"],
  ["Move-ins", "6"],
] as const;

function SyncVisual() {
  return (
    <Panel className="border border-slate-100 bg-slate-50/60">
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-2 font-semibold text-slate-900">
          <img src={OctermMark} alt="" className="h-4 w-4" />
          Synced to Octerm
        </span>
        <span className="flex items-center gap-1 font-medium text-emerald-700">
          <Check className="h-3.5 w-3.5" aria-hidden />
          Up to date
        </span>
      </div>
      <div className="divide-y divide-slate-100 border border-slate-200 bg-white">
        {SYNCED.map(([name, count]) => (
          <div key={name} className="flex items-center justify-between px-3 py-1.5 text-sm">
            <span className="text-slate-600">{name}</span>
            <span className="font-semibold tabular-nums text-slate-900">{count}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function ClaudeVisual() {
  return (
    <Panel className="rounded-lg bg-[#f7f5ef]">
      <p className="ml-auto max-w-[85%] rounded-xl bg-[#ebe7dc] px-3 py-2 text-sm text-[#1f1e1b]">Which facilities are underpriced?</p>
      <div className="flex items-start gap-2.5 pr-2">
        <ClaudeLogo className="mt-0.5 h-4 w-4 shrink-0" />
        <p className="text-sm leading-relaxed text-[#3a3935]">Boise, Round Rock and Mesa. Boise has the most room: $179, up $15.</p>
      </div>
    </Panel>
  );
}

function TeamsMessage({ avatar, name, time, children }: { avatar: ReactNode; name: string; time: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      {avatar}
      <div className="min-w-0 flex-1 rounded-md bg-white px-3 py-1.5 shadow-[0_1px_2px_rgba(0,0,0,0.08)]">
        <p className="text-[11px] text-slate-500">
          <span className="font-semibold text-slate-800">{name}</span> {time}
        </p>
        <p className="mt-0.5 text-[13px] leading-snug text-slate-800">{children}</p>
      </div>
    </div>
  );
}

function TeamsVisual() {
  return (
    <Panel className="gap-2 rounded-lg bg-[#f3f3f6]">
      <TeamsMessage
        name="Dana Price"
        time="9:41 AM"
        avatar={<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#d6d9f5] text-[10px] font-semibold text-[#4f52b2]">DP</span>}
      >
        <span className="rounded-sm bg-[#e8ebfa] px-1 font-medium text-[#4f52b2]">Octerm</span> move-ins since the rate change?
      </TeamsMessage>
      <TeamsMessage
        name="Octerm"
        time="9:41 AM"
        avatar={
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-logo-background">
            <img src={OctermMark} alt="" className="h-4 w-4" />
          </span>
        }
      >
        Steady: 8.1 a week before, 7.8 since.
      </TeamsMessage>
    </Panel>
  );
}

export function Connectors() {
  const ref = useRef<HTMLDivElement>(null);
  useReveal(ref);
  return (
    <section id="connectors" className="scroll-mt-16 bg-white py-24">
      <style>{REVEAL_STYLES}</style>
      <div ref={ref} className="mx-auto max-w-6xl px-5">
        <h2 className="max-w-2xl text-balance text-[clamp(32px,5vw,48px)] font-semibold leading-[1.05] tracking-[-0.03em] text-logo">
          Plugs into what you already use.
        </h2>
        <div className="mt-12 grid gap-4 lg:grid-cols-3">
          <Tile index={0} icon={Database} label="Management software" title="Synced with your software." desc="Nothing to export or enter.">
            <SyncVisual />
          </Tile>
          <Tile index={1} icon={AssistantLogos} label="AI assistants" title="Ask Claude about your markets." desc="Or any assistant that supports MCP.">
            <ClaudeVisual />
          </Tile>
          <Tile index={2} icon={TeamsLogo} label="Microsoft Teams" title="@Octerm in any facility chat." desc="Answers where your team already talks.">
            <TeamsVisual />
          </Tile>
        </div>
      </div>
    </section>
  );
}
